import React, { useCallback, useEffect, useState } from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { getApplicationName } from "utils";
import Spinner from "components/Spinner";
import {
  getThirdPartyApps as apiGetThirdPartyApps,
  setThirdPartyAccess,
} from "services/mportal";

import { Header, Description } from "../styles";
import MemberCard, { MemberInfo } from "./MemberCard";
import { MemberList, LoadingWrapper, ErrorText } from "./styles";

type Props = {
  onMemberConfirmed: () => void;
  setIsLoading: (loading: boolean) => void;
  shouldConfirm?: boolean;
  onConfirmHandled?: () => void;
};

const MemberPage: React.FC<Props> = ({
  onMemberConfirmed,
  setIsLoading,
  shouldConfirm,
  onConfirmHandled,
}: Props) => {
  const { t } = useTranslation();
  const application = getApplicationName();

  const [members, setMembers] = useState<MemberInfo[]>([]);
  const [applicationDisplayName, setApplicationDisplayName] =
    useState<string>(application);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const confirmingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setIsFetching(true);

    (async () => {
      try {
        const data = await apiGetThirdPartyApps();
        if (cancelled) return;
        const appNames = (data.thirdPartyApps || []).map(
          (a: { name: string }) => a.name,
        );
        const matchedApp = (data.thirdPartyApps || []).find(
          (a: { clientId: string }) => a.clientId === application,
        );
        setApplicationDisplayName(matchedApp?.name || application);
        const memberInfos: MemberInfo[] = (data.members || []).map(
          (m: any) => ({
            memberId: m.id,
            memberName: [m.firstName, m.middleName, m.lastName]
              .filter(Boolean)
              .join(" "),
            enabled: !!m.isThirdPartyEnabled,
            connectedApps: m.isThirdPartyEnabled ? appNames : [],
          }),
        );
        setMembers(memberInfos);

        const enabledMember = memberInfos.find(m => m.enabled);
        if (enabledMember) {
          setSelectedMemberId(enabledMember.memberId);
        } else if (memberInfos.length === 1) {
          setSelectedMemberId(memberInfos[0].memberId);
        }
      } catch (e) {
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!selectedMemberId) return;
    if (confirmingRef.current) return;

    confirmingRef.current = true;
    setIsLoading(true);
    try {
      const currentlyEnabled = members.find((m: MemberInfo) => m.enabled);

      // No-op: the already-enabled member is still selected.
      if (currentlyEnabled?.memberId === selectedMemberId) {
        onMemberConfirmed();
        return;
      }

      // Switch: send a single IS request so account_holder_id is resolved
      // before MPortal clears identity_server_account_id during the TurnOff step.
      await setThirdPartyAccess(
        selectedMemberId,
        true,
        currentlyEnabled?.memberId,
      );

      onMemberConfirmed();
    } catch {
      setFetchError(true);
    } finally {
      setIsLoading(false);
      confirmingRef.current = false;
    }
  }, [selectedMemberId, members, onMemberConfirmed, setIsLoading]);

  useEffect(() => {
    if (!shouldConfirm) return;
    let cancelled = false;

    (async () => {
      await handleConfirm();
      if (!cancelled) onConfirmHandled?.();
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldConfirm, handleConfirm, onConfirmHandled]);

  if (isFetching) {
    return (
      <LoadingWrapper>
        <Spinner />
      </LoadingWrapper>
    );
  }

  if (fetchError) {
    return <ErrorText>{t("memberPage:error")}</ErrorText>;
  }

  if (members.length === 0) {
    return <ErrorText>{t("memberPage:noMembers")}</ErrorText>;
  }

  const isSingleMember = members.length === 1;

  return (
    <>
      <Header>
        {isSingleMember
          ? t("memberPage:confirmHeader")
          : t("memberPage:selectHeader")}
      </Header>
      <Description>
        {isSingleMember ? (
          t("memberPage:confirmBody", { application: applicationDisplayName })
        ) : (
          <>
            <span style={{ display: "block" }}>
              {t("memberPage:selectBodyLine1", {
                application: applicationDisplayName,
              })}
            </span>
            <span style={{ display: "block", marginTop: "12px" }}>
              {t("memberPage:selectBodyLine2")}
            </span>
            <span style={{ display: "block", marginTop: "12px" }}>
              {t("memberPage:selectBodyLine3")}
            </span>
          </>
        )}
      </Description>
      <MemberList role={!isSingleMember ? "radiogroup" : undefined}>
        {members.map((member: MemberInfo) => (
          <MemberCard
            key={member.memberId}
            member={member}
            isSelected={!isSingleMember && member.memberId === selectedMemberId}
            isSelectable={!isSingleMember}
            onSelect={setSelectedMemberId}
          />
        ))}
      </MemberList>
    </>
  );
};

export { MemberPage };
export default MemberPage;
